import { FC, useCallback, useEffect, useState } from 'react'

import { Button, ButtonVariants } from '@inno-ui/button'
import {
  OtpInput,
  OtpInputSizes,
  OtpInputVariants
} from 'modules/auth/components/OtpInput'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLoginMutation } from 'shared/api/auth/requests'
import { useCheckApprovalStatus } from 'shared/api/auth/requests/2fa/checkApprovalStatus'
import { useSendGuestDeactivationCode } from 'shared/api/auth/requests/2fa/guestDisableTwoFactor'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { SessionStorageKey } from 'shared/constants/sessionStorage'
import { DEFAULT_REMEMBER_ME } from 'shared/constants/user'

import { LoginMode } from '../../LoginPage.types'
import styles from './OtpView.module.scss'
import { OtpViewProps } from './OtpView.types'

export const OtpView: FC<OtpViewProps> = ({
  mobileApprovalSessionId,
  setLoginMode
}) => {
  const [otp, setOtp] = useState('')
  const [hasError, setHasError] = useState(false)
  const { t } = useTranslation('auth', {
    keyPrefix: 'new_login.login.otp'
  })
  const navigate = useNavigate()

  const loginMutation = useLoginMutation({
    onError: () => {
      setOtp('')
      setHasError(true)
    }
  })

  const onComplete = useCallback(
    (value: string) => {
      loginMutation.mutate({
        email:
          sessionStorage.getItem(SessionStorageKey.OTP_LOGGING_IN_EMAIL) ?? '',
        password:
          sessionStorage.getItem(SessionStorageKey.OTP_LOGGING_IN_PASSWORD) ??
          '',
        otp: value,
        remember_me: FEATURE_FLAGS.SHOW_REMEMBER_ME
          ? sessionStorage.getItem(
              SessionStorageKey.OTP_LOGGING_IN_REMEMBER_ME
            ) === 'true'
          : DEFAULT_REMEMBER_ME
      })
    },
    [loginMutation]
  )

  useEffect(() => {
    if (otp) setHasError(false)
  }, [otp])

  const onSendDeactivationCodeSuccess = useCallback(() => {
    setLoginMode(LoginMode.DeactivateOtp)
  }, [setLoginMode])

  const {
    mutation: {
      mutate: sendGuestDeactivationCodeMutate,
      isPending: isLoadingDeactivateMailRequest
    }
  } = useSendGuestDeactivationCode({ onSuccess: onSendDeactivationCodeSuccess })

  const handleDeactivateClick = useCallback(() => {
    const email =
      sessionStorage.getItem(SessionStorageKey.OTP_LOGGING_IN_EMAIL) ?? ''
    sendGuestDeactivationCodeMutate({ email })
  }, [sendGuestDeactivationCodeMutate])

  const { data: approvalStatus } = useCheckApprovalStatus(
    {
      sessionId: mobileApprovalSessionId!
    },
    {
      enabled: !!mobileApprovalSessionId,
      refetchInterval: response => {
        return response?.status !== 'pending' ? false : 2000
      }
    }
  )

  useEffect(() => {
    switch (approvalStatus?.status) {
      case 'approved':
        onComplete(approvalStatus.otp)
        break
      case 'rejected':
        // TODO: remove this once we have a proper UI for the rejected state
        navigate(-1)
        break
    }

    // not adding onComplete because for some reason loginMutation
    // is not a stable reference, so I am disabling the eslint rule
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStatus, navigate])

  if (approvalStatus?.status === 'rejected') {
    const illustrationUrl = `${process.env.REACT_APP_UIKIT_CDN_URL}/illustrations/scared-man.png`
    return (
      <div className={styles.mobileApprovalRejected}>
        <img
          src={illustrationUrl}
          alt='Scared man illustration'
          className={styles.illustration}
        />
        <h1 className={styles.title}>{t('mobile_approval_rejected.title')}</h1>
        <p className={styles.description}>
          {t('mobile_approval_rejected.description')}
        </p>
      </div>
    )
  }

  return (
    <div className={styles.otpView}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.enterCodeDescription}>
            {t('enter_code_description')}
          </p>
          {!!mobileApprovalSessionId &&
            (approvalStatus?.status === 'pending' ||
              !approvalStatus?.status) && (
              <p className={styles.mobileApprovalDescription}>
                {t('mobile_approval_description')}
              </p>
            )}
        </div>
        <OtpInput
          autoFocus
          value={otp}
          onChange={setOtp}
          isLoading={loginMutation.isPending}
          onComplete={onComplete}
          hasError={hasError}
          size={OtpInputSizes.L}
          variant={OtpInputVariants.Dark}
        />
        <div className={styles.deactivateTwoFactorLinkContainer}>
          <p className={styles.deactivateTwoFactorLinkText}>
            {t('having_trouble')}
          </p>
          <Button
            variant={ButtonVariants.Text}
            onClick={handleDeactivateClick}
            bold
            label={t('deactivate_two_factor_button')}
            disabled={isLoadingDeactivateMailRequest}
          />
        </div>
      </div>
    </div>
  )
}
